import { useEffect, useRef } from 'react'
import { useAttentionAcknowledge, useAttentionPending } from '../context/AttentionContext'
import { attentionTitle, isTabActive, topReason, type AttentionReason } from '../utils/attentionStore'

export const FLASH_INTERVAL_MS = 1000

export function useDocumentTitle(baseTitle: string, resetTitle: string): void {
  const pending = useAttentionPending()
  const reason = topReason(pending)
  const acknowledge = useAttentionAcknowledge()
  const baseRef = useRef(baseTitle)
  const resetRef = useRef(resetTitle)
  const reasonRef = useRef<AttentionReason | null>(reason)
  const flashingRef = useRef(false)
  const showingAttentionRef = useRef(false)

  useEffect(() => {
    baseRef.current = baseTitle
    resetRef.current = resetTitle
    document.title = flashingRef.current && showingAttentionRef.current && reasonRef.current
      ? attentionTitle(baseTitle, reasonRef.current)
      : baseTitle
  }, [baseTitle, resetTitle])

  useEffect(() => {
    reasonRef.current = reason
    if (reason === null) {
      if (flashingRef.current) {
        flashingRef.current = false
        showingAttentionRef.current = false
        document.title = baseRef.current
      }
      return
    }

    if (isTabActive()) {
      acknowledge()
      return
    }

    flashingRef.current = true
    showingAttentionRef.current = true
    document.title = attentionTitle(baseRef.current, reason)
    const interval = window.setInterval(() => {
      if (isTabActive()) {
        acknowledge()
        return
      }
      showingAttentionRef.current = !showingAttentionRef.current
      document.title = showingAttentionRef.current
        ? attentionTitle(baseRef.current, reasonRef.current ?? reason)
        : baseRef.current
    }, FLASH_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [reason, acknowledge])

  useEffect(() => () => {
    document.title = resetRef.current
  }, [])
}
